import React from 'react';
import { motion } from 'framer-motion';
import { Icon } from '../ui';

const container = {
  hidden: {},
  show: { transition: { staggerChildren: 0.15, delayChildren: 0.3 } },
};

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  show: { opacity: 1, y: 0, transition: { duration: 0.6, ease: 'easeOut' } },
};

const stats = [
  { value: '99.9%', label: 'Disponibilité', icon: 'IconCloudCheck' },
  { value: '<2min', label: 'Temps de réponse', icon: 'IconBolt' },
  { value: '15+', label: 'Cabinets', icon: 'IconBuildingSkyscraper' },
  { value: '0', label: 'Incidents sécurité', icon: 'IconShieldCheck' },
];

function AnimatedCounter({ to }) {
  const [count, setCount] = React.useState(0);
  const ref = React.useRef(null);
  const inView = React.useRef(false);

  React.useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !inView.current) {
          inView.current = true;
          const duration = 1500;
          const start = performance.now();
          const raw = parseFloat(String(to).replace(/[^0-9.]/g, ''));
          const suffix = String(to).replace(/[0-9.]/g, '');
          const frame = (now) => {
            const elapsed = now - start;
            const progress = Math.min(elapsed / duration, 1);
            const eased = 1 - Math.pow(1 - progress, 3);
            setCount(raw * eased);
            if (progress < 1) requestAnimationFrame(frame);
          };
          requestAnimationFrame(frame);
        }
      },
      { threshold: 0.5 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [to]);

  const raw = parseFloat(String(to).replace(/[^0-9.]/g, ''));
  const suffix = String(to).replace(/[0-9.]/g, '');
  const display = count < raw ? (raw % 1 === 0 ? Math.floor(count) : count.toFixed(1)) : raw;

  return <span ref={ref}>{display}{suffix}</span>;
}

export default function HeroSection({ onSignup }) {
  return (
    <motion.section variants={container} initial="hidden" animate="show" className="pt-40 pb-20 text-center max-w-5xl mx-auto mb-24">
      <motion.div variants={fadeUp} className="inline-flex items-center gap-3 px-5 py-2.5 rounded-full bg-gradient-to-r from-slate-800/60 to-slate-900/60 border border-slate-700/50 text-cyan-300 text-sm font-semibold mb-10 backdrop-blur-xl shadow-lg">
        <span className="w-2.5 h-2.5 rounded-full bg-gradient-to-r from-green-400 to-emerald-400 animate-pulse shadow-lg shadow-green-400/30" />
        <span>Conforme Loi de Finances 2026</span>
      </motion.div>

      <motion.h1 variants={fadeUp} className="text-5xl sm:text-6xl lg:text-7xl xl:text-8xl font-black text-white tracking-tight mb-10 leading-[1.1]">
        <span className="block">L'excellence</span>
        <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-400 via-blue-400 to-cyan-400 bg-[length:200%_auto] animate-gradient">comptable réinventée</span>
      </motion.h1>

      <motion.p variants={fadeUp} className="text-2xl text-slate-300 mb-12 leading-relaxed max-w-3xl mx-auto font-light">
        Solution tout-en-un pour cabinets et PME. Automatisation, conformité et insights en temps réel.
      </motion.p>

      <motion.div variants={fadeUp} className="flex flex-wrap justify-center gap-6">
        <button
          onClick={onSignup}
          className="inline-flex items-center gap-2 px-10 py-5 rounded-2xl bg-gradient-to-r from-white to-slate-100 text-slate-900 font-bold text-lg hover:shadow-2xl hover:shadow-white/20 hover:-translate-y-1 transition-all duration-300"
        >
          Créer un compte Cabinet
        </button>
        <button
          onClick={() => document.getElementById('modules')?.scrollIntoView({ behavior: 'smooth' })}
          className="inline-flex items-center gap-2 px-10 py-5 rounded-2xl border-2 border-slate-600 text-slate-200 font-medium text-lg hover:border-indigo-400 hover:text-white hover:bg-slate-800/40 transition-all duration-300"
        >
          <Icon name="IconArrowRight" size={20} />
          Découvrir les modules
        </button>
      </motion.div>

      <motion.div variants={fadeUp} className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-24">
        {stats.map((stat) => (
          <div key={stat.label} className="bg-slate-900/40 backdrop-blur-xl border border-slate-700/50 rounded-2xl p-6 text-center hover:border-indigo-500/30 transition-all duration-300 group">
            <Icon name={stat.icon} size={32} className="mx-auto mb-3 text-indigo-400 group-hover:text-cyan-400 transition-colors duration-500" />
            <div className="text-3xl font-bold bg-gradient-to-r from-white to-slate-200 bg-clip-text text-transparent group-hover:from-indigo-400 group-hover:to-cyan-400 transition-all duration-500">
              <AnimatedCounter to={stat.value} />
            </div>
            <div className="text-sm text-slate-400 mt-2">{stat.label}</div>
          </div>
        ))}
      </motion.div>
    </motion.section>
  );
}
